import type { ComponentChildren } from 'preact';
import type { Tab } from '@hud/core';
import './tabbar.css';

export interface TabBarProps {
  tabs: Tab[];
  /** Dashboard name currently showing, used to mark the active tab. */
  active: string;
  /** Rendered at the end of the bar — the connection gear lives here. */
  trailing?: ComponentChildren;
}

/**
 * App-wide bottom navigation, driven by `tabs:` in `config.md`.
 *
 * Chrome rather than a widget. The `nav` widget still exists for links placed
 * deliberately inside a dashboard, but primary navigation shouldn't cost grid
 * cells on every screen or have to be repeated in every dashboard file.
 *
 * Renders nothing when no tabs are configured, so this is invisible to vaults
 * that never opt in.
 */
export function TabBar({ tabs, active, trailing }: TabBarProps) {
  if (tabs.length === 0) return null;

  return (
    <nav class="hud-tabs" aria-label="Primary">
      {tabs.map((tab) => {
        const current = tab.to === active;
        return (
          <a
            class={current ? 'hud-tabs__tab hud-tabs__tab--active' : 'hud-tabs__tab'}
            key={tab.to}
            href={`#/${tab.to}`}
            aria-current={current ? 'page' : undefined}
          >
            {tab.icon ? (
              <span class="hud-tabs__icon" aria-hidden="true">
                {tab.icon}
              </span>
            ) : null}
            <span class="hud-tabs__label">{tab.label}</span>
          </a>
        );
      })}
      {trailing ? <div class="hud-tabs__trailing">{trailing}</div> : null}
    </nav>
  );
}
